import { AirParticles } from '@/components/Layout';
import { Layers, Cpu, Wifi, Shield, BarChart3, Zap } from 'lucide-react';

export default function Technology() {
  const layers = [
    { icon: Shield, title: 'Multi-Stage Filtration', desc: 'Pre-filter, HEPA H13, and activated carbon layers remove 99.5% of PM2.5 particles and harmful gases.', color: 'var(--deep-teal)' },
    { icon: Cpu, title: 'AI-Powered Control', desc: 'Machine learning algorithms predict air quality patterns and optimize ventilation schedules automatically.', color: 'var(--aqua)' },
    { icon: Wifi, title: 'IoT Mesh Network', desc: 'All Vayu devices communicate via a secure mesh network, enabling whole-building air quality orchestration.', color: '#8B5CF6' },
    { icon: BarChart3, title: 'Real-Time Analytics', desc: 'Continuous monitoring of 6 pollutants with cloud-based analytics, trends, and predictive alerts.', color: '#F59E0B' },
    { icon: Zap, title: 'Energy Efficiency', desc: 'Ultra-low power consumption (12W per unit). Smart scheduling reduces HVAC dependency by up to 30%.', color: 'var(--green-good)' },
    { icon: Layers, title: 'Modular Architecture', desc: 'Plug-and-play design. Add or remove units without rewiring. Scales from 1 room to 1000 units.', color: '#EF4444' },
  ];

  return (
    <div>
      <section className="relative pt-32 pb-20 px-6 lg:px-12 bg-teal-gradient overflow-hidden" data-testid="tech-hero">
        <AirParticles count={20} color="rgba(63,167,214,0.1)" />
        <div className="max-w-5xl mx-auto text-center relative z-10">
          <div className="text-xs font-semibold uppercase tracking-[0.25em] mb-4" style={{ color: 'var(--aqua)' }}>Technology</div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tight text-white mb-6 animate-fade-up" style={{ fontFamily: 'Inter' }} data-testid="tech-title">
            Engineering air<br />at the molecular level.
          </h1>
          <p className="text-base lg:text-lg text-white/50 max-w-2xl mx-auto animate-fade-up delay-200">
            Vayu combines advanced filtration, artificial intelligence, and IoT infrastructure to deliver the cleanest indoor air possible.
          </p>
        </div>
      </section>

      <section className="section-padding px-6 lg:px-12" data-testid="tech-layers">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <div className="overline-teal mb-4">Core Technology</div>
            <h2 className="text-3xl md:text-4xl font-semibold tracking-tight" style={{ fontFamily: 'Inter' }}>Six layers of innovation.</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {layers.map((l) => (
              <div key={l.title} className="card-hover p-8 animate-fade-up" data-testid={`tech-layer-${l.title}`}>
                <div className="w-12 h-12 rounded-2xl flex items-center justify-center mb-5" style={{ background: `${l.color}10` }}>
                  <l.icon className="w-5 h-5" style={{ color: l.color }} strokeWidth={1.5} />
                </div>
                <h3 className="text-lg font-semibold mb-2" style={{ fontFamily: 'Inter' }}>{l.title}</h3>
                <p className="text-sm leading-relaxed" style={{ color: 'var(--text-secondary)' }}>{l.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="section-padding px-6 lg:px-12 bg-subtle" data-testid="how-it-works">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <div className="overline-teal mb-4">How It Works</div>
            <h2 className="text-3xl md:text-4xl font-semibold tracking-tight" style={{ fontFamily: 'Inter' }}>Simple. Intelligent. Effective.</h2>
          </div>
          <div className="space-y-0">
            {[
              { step: '01', title: 'Install Vayu Pane', desc: 'Replaces your existing window panel. No structural changes. 30-minute installation.' },
              { step: '02', title: 'Connect Vayu Sense', desc: 'Place the sensor indoors. It automatically connects to your Vayu network.' },
              { step: '03', title: 'Breathe Clean Air', desc: 'The system monitors outdoor AQI and automatically manages airflow to maintain pristine indoor quality.' },
            ].map((s) => (
              <div key={s.step} className="flex gap-6 items-start py-8" style={{ borderBottom: s.step !== '03' ? '1px solid var(--border)' : 'none' }} data-testid={`step-${s.step}`}>
                <span className="stat-number text-5xl" style={{ color: 'var(--aqua)', opacity: 0.3 }}>{s.step}</span>
                <div>
                  <h3 className="text-xl font-semibold mb-2" style={{ fontFamily: 'Inter' }}>{s.title}</h3>
                  <p className="text-sm leading-relaxed" style={{ color: 'var(--text-secondary)' }}>{s.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
